import React, { useState } from "react";
import { Icon, Switch, Spin } from "antd";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import Colors from "../../../../../themes/Colors";
import { resolveColor } from '../../../../../themes/theme';
import { buildConsumoSeries } from "../../utils/consumoData";
import { formatMoney } from "../../utils/campaignDetailData";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const BAR_PAST = Colors.primary;
// Canvas no entiende var(--dsp-*): se resuelve al armar el dataset
const getBarFuture = () => resolveColor(Colors.extraLightPrimary);
const LINE_PAST = "#1e4fd6";
const LINE_FUTURE = "#93c5fd";

const numberFmt = (v) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const ConsumoDiarioCard = ({ stats, loading, campaign, conversionRates }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [showCampaignCur, setShowCampaignCur] = useState(true);

  const campaignCurrency = campaign.currency || "ARS";
  const rates = conversionRates || [];
  const rateOf = (cur) =>
    cur === "USD" ? (rates.find((r) => r.currency === "USD") || { rate: 1 }).rate : (rates.find((r) => r.currency === cur) || {}).rate;
  const canConvert = !!rateOf(campaignCurrency);

  const currency = showCampaignCur || !canConvert ? campaignCurrency : "USD";
  const sourceRate = rateOf(campaignCurrency);
  const targetRate = rateOf(currency);
  const factor = sourceRate && targetRate ? targetRate / sourceRate : 1;

  const series = buildConsumoSeries(stats);

  const labels = series ? series.points.map((p) => p.label) : [];
  const lastPast = series ? series.lastPastIndex : -1;

  const chartData = series && {
    labels,
    datasets: [
      {
        type: "bar",
        data: series.points.map((p) => p.value * factor),
        backgroundColor: series.points.map((p) => (p.isPast ? BAR_PAST : getBarFuture())),
        borderRadius: 4,
        maxBarThickness: 34,
        order: 2,
      },
      {
        type: "line",
        data: series.points.map((p, i) => (lastPast >= 0 && i <= lastPast ? p.cumulative * factor : null)),
        borderColor: LINE_PAST,
        borderWidth: 2.5,
        tension: 0.35,
        pointRadius: series.points.map((_, i) => (i === lastPast ? 4 : 0)),
        pointBackgroundColor: LINE_PAST,
        order: 1,
      },
      {
        type: "line",
        data: series.points.map((p, i) => (lastPast >= 0 && i >= lastPast ? p.cumulative * factor : null)),
        borderColor: LINE_FUTURE,
        borderWidth: 2.5,
        tension: 0.35,
        pointRadius: series.points.map((_, i) => (i === series.points.length - 1 ? 4 : 0)),
        pointBackgroundColor: LINE_FUTURE,
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => formatMoney(ctx.parsed.y, currency) },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: resolveColor(Colors.border), borderDash: [4, 4] },
        ticks: { callback: (v) => numberFmt(v) },
      },
      x: { grid: { display: false } },
    },
  };

  return (
    <div className="campaignDetail__card consumo">
      <div className="consumo__head">
        <h3 className="campaignDetail__section-title consumo__title">
          {t("Daily consumption of the campaign")}
        </h3>
        <button type="button" className="consumo__toggle" onClick={() => setOpen(!open)}>
          {open ? t("Hide") : t("Show")} <Icon type={open ? "up" : "down"} />
        </button>
      </div>

      {open && (
        <div className="consumo__panel">
          <div className="consumo__panel-head">
            <span className="consumo__subtitle">
              {t("Total consumption of all screens (per day)")}
            </span>
            <div className="consumo__currency">
              <span className={`consumo__cur ${!showCampaignCur ? "consumo__cur--on" : "consumo__cur--off"}`}>
                USD
              </span>
              <Switch
                size="small"
                checked={showCampaignCur}
                disabled={!canConvert}
                onChange={setShowCampaignCur}
              />
              <span className={`consumo__cur ${showCampaignCur ? "consumo__cur--on" : "consumo__cur--off"}`}>
                {campaignCurrency}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="consumo__loading">
              <Spin size="large" />
            </div>
          ) : series ? (
            <>
              <div className="consumo__chart">
                <Chart type="bar" data={chartData} options={options} />
              </div>
              <div className="consumo__legend">
                <div className="consumo__legend-left">
                  <span className="consumo__legend-item">
                    <i className="consumo__sw consumo__sw--bar" />
                    {t("consumption_per_day", { currency })}
                  </span>
                  <span className="consumo__legend-item">
                    <i className="consumo__sw consumo__sw--line" />
                    {t("consumption_accumulated", { currency })}
                  </span>
                </div>
                <div className="consumo__legend-right">
                  <span className="consumo__total-label">{t("Total campaign")}</span>
                  <span className="consumo__total-value">{formatMoney(series.total * factor, currency)}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="consumo__empty">{t("No data")}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsumoDiarioCard;
